import React, {
	type ComponentType,
	createContext,
	type PropsWithChildren,
	type ReactNode,
	useContext,
} from 'react';

import { type ExtensionParams } from '@atlaskit/editor-common/extensions';
import { type MediaProvider } from '@atlaskit/editor-common/provider-factory';
import type { Auth } from '@atlaskit/media-core';
import type { UploadRejectionData } from '@atlaskit/media-picker';

import { type ExtensionMode, type FeedbackType } from '../../types';

export interface ExperienceTracker<BaseStartParams = { analyticsSource: string }> {
	start: (params: BaseStartParams) => void;
	abort?: (reason: { reason: string }) => void;
	succeed: () => void;
	stopOnError: ({ error }: { error: Error }) => void;
}

type AiDescriptionStartParams = { analyticsSource: string; confluencePageId: string };
export type AiDescriptionExperienceTracker = ExperienceTracker<AiDescriptionStartParams>;

export interface ProductDependenciesProviderProps {
	mode: ExtensionMode;
	extensionNode?: ExtensionParams<Record<string, any>>;
	contentId: string;
	children?: ReactNode;
}

export interface ProductDependenciesProviderResponse {
	// ~ REQUIRED DEPENDENCIES ~

	cloudId: string;
	userId: string;
	isCompanyHub: boolean;
	smartCardProviderComponent: ComponentType<PropsWithChildren<{}>>;

	// Media Providers
	createRendererMediaProvider?: (options: {
		contentId: string;
		isExternalShare: boolean;
	}) => Promise<MediaProvider>;
	createEditorMediaProvider?: (options: {
		contentId: string;
		isExternalShare: boolean;
		contentType: string;
		onUploadRejection?: ((data: UploadRejectionData) => boolean) | undefined;
	}) => Promise<MediaProvider>;

	// Emoji
	lazyEmojiComponentLoader: any;

	// Theming
	isSSRProductInDarkMode?: boolean;

	// User segmentation
	useSegmentationTargetGroups: ({
		nextCursor,
		initialGroupIds,
	}: {
		nextCursor?: string;
		initialGroupIds?: string[];
	}) => any;
	isTargetAudience: (targetGroupIds: string[] | undefined) => boolean;

	// ~ OPTIONAL DEPENDENCIES ~

	// AI and Company Hub
	isAdminHubAIEnabled?: boolean;

	// Error Handling
	submitError?: (error: Error) => void;
	markErrorAsHandled?: (error: Error) => void;
	errorBoundaryComponent?: ComponentType<PropsWithChildren<{}>>;
	errorDisplayComponent?: ComponentType<PropsWithChildren<{ error: Error }>>;

	// Shortcut listener
	shortcutListener?: ComponentType<{ accelerator: string; listener: () => void }>;

	// Analytics & Feedback
	analyticsContext?: Record<string, any>;
	useAnalyticsActions?: { [key: string]: any };
	useFeedbackCollector?: (options: { source: string; type: FeedbackType }) => () => void;

	// Premium upsells
	ConfigUpsellScreen?: ComponentType<{ feature: string }>;

	// Experience Tracking
	experienceStartComponent?: ComponentType<{}>;
	experienceSuccessComponent?: ComponentType<{}>;
	experienceStopComponent?: ComponentType<{ error?: Error }>;
	mediaUploadExperienceTracker?: ExperienceTracker;
	aiDescriptionExperienceTracker?: AiDescriptionExperienceTracker;

	// Custom search configuration
	orgId?: string;
	displayName?: string;
	searchConfigProvider?: {
		peopleDirectoryUrl?: string;
		advancedSearchUrl?: string;
		searchSessionId?: string;
	};
}

export const ProductDependenciesContext = createContext<ProductDependenciesProviderResponse>({
	// AI and Company Hub
	isCompanyHub: true,
	isAdminHubAIEnabled: false,

	// Shortcuts
	shortcutListener: undefined,

	// Smart cards
	smartCardProviderComponent: () => null,
	lazyEmojiComponentLoader: () => null,

	// Custom search configuration
	orgId: '',
	userId: '',
	searchConfigProvider: {
		peopleDirectoryUrl: '',
		advancedSearchUrl: '',
		searchSessionId: '',
	},
	displayName: '',

	// Theming
	isSSRProductInDarkMode: undefined,

	// Feedback collection
	useFeedbackCollector: undefined,

	// Media providers
	cloudId: '',
	createRendererMediaProvider: undefined,
	createEditorMediaProvider: () =>
		Promise.resolve({
			viewMediaClientConfig: {
				authProvider: async () => ({}) as Auth,
			},
			uploadMediaClientConfig: {
				authProvider: async () => ({}) as Auth,
			},
		} as MediaProvider),

	// Error handling
	submitError: () => {},
	markErrorAsHandled: () => {},
	errorDisplayComponent: () => null,
	errorBoundaryComponent: undefined,

	// Premium upsells
	ConfigUpsellScreen: undefined,

	// Experience tracking
	mediaUploadExperienceTracker: undefined,
	aiDescriptionExperienceTracker: undefined,
	experienceSuccessComponent: () => null,
	experienceStopComponent: () => null,
	experienceStartComponent: () => null,

	// Analytics
	analyticsContext: {},

	// User segmentation
	useSegmentationTargetGroups: () => {},
	isTargetAudience: () => false,
});

export const ProductDependenciesProvider = ({
	children,
	...props
}: PropsWithChildren<ProductDependenciesProviderProps>) => {
	const defaultValue = useContext(ProductDependenciesContext);
	const value = { ...defaultValue, ...props };

	return (
		<ProductDependenciesContext.Provider value={value}>
			{children}
		</ProductDependenciesContext.Provider>
	);
};

export function useProductDependencies(): ProductDependenciesProviderResponse {
	const ctx = useContext<ProductDependenciesProviderResponse>(ProductDependenciesContext);
	if (!ctx) {
		throw new Error('useProductDependencies must be used within ProductDependenciesProvider');
	}
	return ctx;
}
